import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Plus, Trash2, Download, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import BrandLogo from '../../components/BrandLogo';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import EmailGate, { useEmailGate } from '../../components/EmailGate';

const currency = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function InvoiceGeneratorPage() {
  const navigate = useNavigate();
  const [seller, setSeller] = useState({ name: 'AroundTheWorld.AI', email: 'hello@aroundtheworld.ai', address: 'Global HQ · Delhi · Dubai · NYC' });
  const [buyer, setBuyer] = useState({ name: 'Acme Corp', email: 'accounts@acme.com', address: '123 Ocean Ave, San Francisco, CA' });
  const [invoiceNo, setInvoiceNo] = useState(`INV-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000 + 1000)}`);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10));
  const [items, setItems] = useState([
    { description: 'AI dev studio subscription — Pro tier', qty: 1, price: 84 },
    { description: 'Custom onboarding & workshop', qty: 4, price: 250 },
  ]);
  const [tax, setTax] = useState(10);
  const [notes, setNotes] = useState('Thank you for your business. Wire transfer or Stripe accepted.');
  const pdfRef = useRef(null);
  const gate = useEmailGate('invoice');

  const subtotal = items.reduce((s, i) => s + Number(i.qty || 0) * Number(i.price || 0), 0);
  const taxAmount = subtotal * (Number(tax || 0) / 100);
  const total = subtotal + taxAmount;

  const addItem = () => setItems([...items, { description: '', qty: 1, price: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    setItems(next);
  };

  const download = async () => {
    gate.run(async () => {
      if (!pdfRef.current) return;
      toast({ title: 'Preparing PDF…' });
      const canvas = await html2canvas(pdfRef.current, { scale: 2, backgroundColor: '#ffffff' });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, 'PNG', 0, 0, w, h, undefined, 'FAST');
      pdf.save(`${invoiceNo}.pdf`);
      toast({ title: 'Invoice downloaded ✅' });
    });
  };

  return (
    <div className="min-h-screen bg-[#050818] text-white">
      <div className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-4">
          <button data-testid="invoice-back" onClick={() => navigate('/')} className="text-white/70 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft size={16} /> Home
          </button>
          <span className="text-white/25">/</span>
          <span className="text-sm text-white/80">Templates</span>
          <span className="text-white/25">/</span>
          <span className="text-sm text-emerald-300 font-semibold">Invoice Generator</span>
          <div className="ml-auto"><BrandLogo size={24} tone="light" /></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[420px_1fr] gap-8">
        {/* LEFT: form */}
        <div className="space-y-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 mb-4">
              <Receipt size={13} className="text-emerald-300" />
              <span className="text-[11px] tracking-widest uppercase font-bold text-white/85">Invoice Generator</span>
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">Create a pro invoice</h1>
            <p className="text-sm text-white/60 mt-1.5">Fill it in. Download a clean PDF. 100% free, browser-side.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Invoice #</label>
              <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} className="mt-2 w-full h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-emerald-400 outline-none text-sm" />
            </div>
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Tax %</label>
              <input type="number" value={tax} onChange={(e) => setTax(e.target.value)} className="mt-2 w-full h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-emerald-400 outline-none text-sm" />
            </div>
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Issued</label>
              <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="mt-2 w-full h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-emerald-400 outline-none text-sm" />
            </div>
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Due</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-2 w-full h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-emerald-400 outline-none text-sm" />
            </div>
          </div>

          <PartyEditor title="Bill from" party={seller} onChange={setSeller} testId="seller" />
          <PartyEditor title="Bill to" party={buyer} onChange={setBuyer} testId="buyer" />

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Line Items</label>
              <button data-testid="invoice-add-item" onClick={addItem} className="text-emerald-300 text-xs font-bold flex items-center gap-1"><Plus size={12} /> Add</button>
            </div>
            {items.map((it, i) => (
              <div key={i} data-testid={`invoice-item-${i}`} className="p-3 rounded-xl bg-white/4 border border-white/10 mb-2 space-y-2">
                <input value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)} placeholder="Description" className="w-full h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-emerald-400 outline-none text-sm" />
                <div className="grid grid-cols-[80px_1fr_auto] gap-2 items-center">
                  <input type="number" value={it.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} placeholder="Qty" className="h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-emerald-400 outline-none text-sm font-mono" />
                  <input type="number" value={it.price} onChange={(e) => updateItem(i, 'price', e.target.value)} placeholder="Price" className="h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-emerald-400 outline-none text-sm font-mono" />
                  {items.length > 1 && <button onClick={() => removeItem(i)} className="text-rose-300 hover:text-rose-200"><Trash2 size={14} /></button>}
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Notes / Terms</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-2 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 focus:border-emerald-400 outline-none text-sm" />
          </div>

          <Button data-testid="invoice-download" onClick={download} className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-400 to-teal-500 text-black font-bold">
            <Download size={16} className="mr-2" /> Download PDF
          </Button>
        </div>

        {/* RIGHT: Preview */}
        <div>
          <div ref={pdfRef} className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: '#ffffff', color: '#0f172a' }}>
            {/* Header */}
            <div className="p-8 flex items-start justify-between border-b border-slate-200" style={{ background: 'linear-gradient(135deg, #ecfeff 0%, #f0fdfa 50%, #ffffff 100%)' }}>
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-emerald-600 font-bold mb-1">Invoice</div>
                <div className="font-display text-4xl font-black tracking-tighter">{invoiceNo}</div>
                <div className="mt-3 text-xs text-slate-500 font-mono">
                  Issued {issueDate} · Due {dueDate}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-lg leading-tight">{seller.name}</div>
                <div className="text-xs text-slate-500 mt-1">{seller.email}</div>
                <div className="text-xs text-slate-500 mt-0.5 max-w-[220px]">{seller.address}</div>
              </div>
            </div>

            {/* Parties */}
            <div className="p-8 grid grid-cols-2 gap-6 border-b border-slate-100 bg-slate-50/50">
              <div>
                <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Bill to</div>
                <div className="mt-2 font-bold text-slate-900">{buyer.name}</div>
                <div className="text-sm text-slate-600 mt-1">{buyer.email}</div>
                <div className="text-sm text-slate-600">{buyer.address}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Total due</div>
                <div className="mt-2 font-display text-4xl font-black text-slate-900">{currency(total)}</div>
                <div className="text-xs text-slate-500 mt-1">by {dueDate}</div>
              </div>
            </div>

            {/* Items table */}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="px-8 py-3">Description</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3 text-right">Price</th>
                  <th className="px-8 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-8 py-4 font-medium">{it.description}</td>
                    <td className="px-4 py-4 text-right font-mono">{it.qty}</td>
                    <td className="px-4 py-4 text-right font-mono">{currency(it.price)}</td>
                    <td className="px-8 py-4 text-right font-mono font-bold">{currency(Number(it.qty) * Number(it.price))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="p-8 pt-4 border-t border-slate-100">
              <div className="ml-auto max-w-xs space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span><span className="font-mono">{currency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Tax ({tax}%)</span><span className="font-mono">{currency(taxAmount)}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2 font-bold text-slate-900 text-lg">
                  <span>Total</span>
                  <span className="font-mono">{currency(total)}</span>
                </div>
              </div>
            </div>

            {/* Notes + Footer */}
            <div className="px-8 pb-8 pt-4 border-t border-slate-100 text-xs text-slate-600">
              <div className="font-bold uppercase tracking-widest text-[10px] text-slate-500 mb-1">Notes</div>
              {notes}
              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-400">
                <span>Generated by Around the World AI</span>
                <span>aroundtheworld.ai</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toaster />
      <EmailGate {...gate} />
    </div>
  );
}

function PartyEditor({ title, party, onChange, testId }) {
  return (
    <div>
      <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">{title}</label>
      <div className="mt-2 p-3 rounded-xl bg-white/4 border border-white/10 space-y-2">
        <input data-testid={`${testId}-name`} value={party.name} onChange={(e) => onChange({ ...party, name: e.target.value })} placeholder="Name" className="w-full h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-emerald-400 outline-none text-sm" />
        <input data-testid={`${testId}-email`} value={party.email} onChange={(e) => onChange({ ...party, email: e.target.value })} placeholder="Email" className="w-full h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-emerald-400 outline-none text-sm" />
        <input data-testid={`${testId}-address`} value={party.address} onChange={(e) => onChange({ ...party, address: e.target.value })} placeholder="Address" className="w-full h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-emerald-400 outline-none text-sm" />
      </div>
    </div>
  );
}
